import TextInput from "components/inputs/text-input";
import ModalTemplate from "components/modal/modal-template";
import Radio from "components/radio";
import Typography from "components/typography";
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Colors } from "utils/colors";
import { ITrackContributor } from "types/musicData";
import TextDropdown from "components/inputs/text-dropdown";
import api from "services/axiosInstance";
import Toast from "components/toast";
import { IDropItem } from "components/inputs/dropdown";
import { isValidEmail } from "utils/isValidEmail";

interface IProps {
  open: boolean;
  close: () => void;
  confirm: (contributor: ITrackContributor) => void;
  confirmEdit?: (contributor: ITrackContributor) => void;
  trackId?: string;
  editData?: ITrackContributor;
}
const AddContributorModal = ({
  close,
  confirm,
  editData,
  confirmEdit,
  open,
  trackId
}: IProps) => {
  const [name, setName] = useState("");

  const [role, setRole] = useState<"Writer" | "Producer" | "Mix Engineer">(
    "Writer"
  );

  const [confirmLoading, setConfirmLoading] = useState(false);

  const [streamingService, setStreamingService] = useState(false);
  const [spotifyUrl, setSpotifyUrl] = useState("");
  const [appleUrl, setAppleUrl] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [inputsDisabled, setInputsDisabled] = useState(false);

  const [email, setEmail] = useState("");

  const buttonDisabled = useMemo(() => {
    if (
      editData &&
      confirmEdit &&
      trackId &&
      name.length > 2 &&
      isValidEmail(email) &&
      role
    ) {
      return false;
    }

    if (role && name.length > 2 && isValidEmail(email)) {
      return false;
    }
    return true;
  }, [role, name, email, editData, confirmEdit, trackId]);

  const reset = () => {
    setName("");
    setEmail("");
    setRole("Writer");
    setStreamingService(false);
    setSpotifyUrl("");
    setYoutubeUrl("");
    setAppleUrl("");
  };

  useEffect(() => {
    if (editData) {
      setName(editData.name);
      setEmail(editData.email || "");

      setRole(editData.role);
      if (editData.appleUrl || editData.spotifyUrl || editData.youtubeUrl) {
        setStreamingService(true);
        setSpotifyUrl(editData?.spotifyUrl || "");
        setAppleUrl(editData?.appleUrl || "");
        setYoutubeUrl(editData?.youtubeUrl || "");
      }
    }
  }, [editData]);

  //confirm button
  const Confirm = async () => {
    if (confirmLoading) return;
    setConfirmLoading(true);

    try {
      if (editData && confirmEdit && trackId) {
        await api.patch(
          `/collaborators/${trackId}/collaborators/${editData.id}`,
          {
            name,
            role,
            spotifyUrl,
            appleUrl,
            youtubeUrl,
            email,
            id: editData.id
          }
        );
        confirmEdit({
          name,
          role,
          spotifyUrl,
          appleUrl,
          youtubeUrl,
          email,
          id: editData.id
        });
      } else {
        confirm({
          name,
          role,
          spotifyUrl,
          appleUrl,
          youtubeUrl,
          email
        });
      }
      close();
      reset();
    } catch (error) {
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <ModalTemplate
      header={editData ? "Edit Contributor" : "Add Contributor"}
      confirmText={editData ? "Edit Contributor" : "Add Contributor"}
      close={close}
      disableConfirm={buttonDisabled}
      confirm={Confirm}
      loading={confirmLoading}
    >
      <TextInput
        placeholder="Enter Contributor’s name "
        label="Name"
        required
        value={name}
        onChange={(e) => setName(e.target.value)}
      />

      <Spacer />
      <TextInput
        label="Email*"
        placeholder="Enter Contributor’s email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />

      <ContributorTypeItem style={{ marginTop: "4rem" }}>
        <Radio
          value={role === "Writer"}
          onClick={() => {
            setRole("Writer");
          }}
        />
        <Typography color={Colors.white200} ml="0.4">
          Songwriter
        </Typography>
      </ContributorTypeItem>
      <ContributorTypeItem>
        <Radio
          value={role === "Producer"}
          onClick={() => {
            setRole("Producer");
          }}
        />
        <Typography color={Colors.white200} ml="0.4">
          Producer
        </Typography>
      </ContributorTypeItem>
      <ContributorTypeItem>
        <Radio
          value={role === "Mix Engineer"}
          onClick={() => {
            setRole("Mix Engineer");
          }}
        />
        <Typography color={Colors.white200} ml="0.4">
          Mix Engineer
        </Typography>
      </ContributorTypeItem>

      <Typography mt="2.4" weight="medium" mb="1.6">
        Does this artist have music live on streaming services
      </Typography>
      <StreamingServiceRadioContainer>
        <Radio
          value={streamingService}
          onClick={() => setStreamingService(true)}
          disabled={inputsDisabled}
        />
        <Typography color={Colors.white200} ml="0.4" mr="5">
          Yes
        </Typography>
        <Radio
          value={!streamingService}
          onClick={() => setStreamingService(false)}
        />
        <Typography color={Colors.white200} ml="0.4">
          No
        </Typography>
      </StreamingServiceRadioContainer>
      {streamingService && (
        <>
          <StreamingServiceItem>
            <TextInput
              label="Spotify Artist URL"
              placeholder="Enter Spotify Artist Link"
              value={spotifyUrl}
              onChange={(e) => setSpotifyUrl(e.target.value)}
              disabled={inputsDisabled}
            />
          </StreamingServiceItem>
          <StreamingServiceItem>
            <TextInput
              label="Apple Artist URL"
              placeholder="Enter Apple Music Artist Link"
              value={appleUrl}
              onChange={(e) => setAppleUrl(e.target.value)}
              disabled={inputsDisabled}
            />
          </StreamingServiceItem>
          <StreamingServiceItem>
            <TextInput
              label="YouTube Music Artist URL"
              placeholder="Enter YouTube Music Artist Link"
              value={youtubeUrl}
              onChange={(e) => setYoutubeUrl(e.target.value)}
              disabled={inputsDisabled}
            />
          </StreamingServiceItem>
        </>
      )}
    </ModalTemplate>
  );
};

export default AddContributorModal;

const ContributorTypeItem = styled.div`
  display: flex;
  align-items: center;
  margin-top: 2.4rem;
`;

const StreamingServiceRadioContainer = styled.div`
  display: flex;
  align-items: center;
`;

const StreamingServiceItem = styled.div`
  margin-top: 1.6rem;
`;

const Spacer = styled.div`
  height: 4rem;
`;
